<template>
  <SfBannerPage
    :variant="bannerVariant"
    icon-class=""
    class="workflow-review-status-banner tw-group"
    data-testid="workflow-review-status-container"
  >
    <template #default>
      <div class="tw-flex tw-flex-col tw-justify-center">
        <!-- Preparation status (read-only) -->
        <span
          v-tooltip.html.placementBottom.textLeft="preparationTooltip"
          class="tw-text-sf-secondary-light tw-w-fit tw-mb-1"
        >
          {{ preparationLabel }}
        </span>

        <!-- Review status -->
        <div class="tw-flex tw-items-center tw-gap-2">
          <div
            v-if="hasAnyUpToDateReview"
            v-tooltip.html.placementBottom.textLeft="detailedTooltip"
            class="tw-relative tw-flex tw-items-center -tw-top-[1px]"
          >
            <component :is="reviewIconComponent" />
          </div>

          <span
            v-if="reviewed"
            v-tooltip.html.placementBottom.textLeft="detailedTooltip"
            :class="reviewTextClass"
          >
            <WorkflowReviewStatusReviewedBy
              :has-any-up-to-date-review="hasAnyUpToDateReview"
              :reviews-to-show="reviewsToShow"
            />
          </span>
          <span v-else>
            {{ I18n.t('reconciliation_review.no_reviews') }}
          </span>
        </div>
      </div>
    </template>

    <template #actions>
      <!-- Reviewed by user: show static label, unmark on hover -->
      <div
        v-if="hasUpToDateReviewByUser"
        class="tw-relative tw-flex tw-items-center"
      >
        <span class="tw-text-sf-success group-hover:tw-invisible">
          {{ I18n.t('reconciliation_review.reviewed') }}
        </span>
        <!-- sf-button.scss compiles after Tailwind, so its own position:
             relative beats tw-absolute unless marked important. -->
        <SfButtonDs
          class="!tw-absolute tw-right-0 tw-invisible group-hover:tw-visible"
          :data-heap="`${trackingPrefix}-review-status-mark-as-unreviewed`"
          @click.stop.prevent="unmark"
        >
          <span class="tw-whitespace-nowrap">{{
            I18n.t('reconciliation_review.unmark')
          }}</span>
        </SfButtonDs>
      </div>
      <!-- Not reviewed by user -->
      <SfButtonDs
        v-else
        :data-heap="`${trackingPrefix}-review-status-mark-as-reviewed`"
        @click.stop.prevent="markAsReviewed"
      >
        {{ I18n.t('reconciliation_review.mark_as_reviewed') }}
      </SfButtonDs>
    </template>
  </SfBannerPage>
</template>

<script lang="ts" setup>
defineOptions({ inheritAttrs: false })

import { computed, toRef } from 'vue'
import { SfTime } from '@app/adapters/time'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfBannerPage from '@app/shared/vue/components/sf-banner/sf-banner-page.vue'
import WorkflowReviewStatusReviewedBy from '@app/application/workflows/screens/workflow-review/components/workflow-review-status-reviewed-by.vue'
import { getReviewCounterIcon } from '@app/shared/assets/icons/review-counter-icons'
import { bannerVariants } from '@app/shared/types/banner'
import type {
  TemplatePreparation,
  TemplateReview,
} from '@app/application/workflows/types'
import { useReviewTooltip } from '@app/application/workflows/composables/use-review-tooltip'

interface Props {
  hasAnyUpToDateReview: boolean
  hasUpToDateReviewByUser: boolean
  preparation: TemplatePreparation | null
  reviewed: boolean
  reviewsToShow: TemplateReview[]
  trackingPrefix: string
}

const props = withDefaults(defineProps<Props>(), {
  preparation: null,
  reviewsToShow: () => [],
})

const emit =
  defineEmits<{
    'mark-as-reviewed': []
    unmark: []
  }>()

const I18n = useI18n()
const dateFormat = 'DD MMM YYYY'

// Use the shared composable for tooltip formatting
const { detailedTooltip } = useReviewTooltip({
  reviews: toRef(props, 'reviewsToShow'),
})

// New UI computed properties
const bannerVariant = computed(() => {
  if (props.hasUpToDateReviewByUser) {
    return bannerVariants.success
  }
  if (props.hasAnyUpToDateReview) {
    return bannerVariants.neutral
  }
  if (props.reviewed) {
    return bannerVariants.warning
  }
  return bannerVariants.neutral
})

const preparationLabel = computed(() => {
  return props.preparation
    ? I18n.t('reconciliation_preparation.prepared')
    : I18n.t('reconciliation_preparation.not_marked_as_prepared')
})

const preparationTooltip = computed(() => {
  if (!props.preparation) {
    return ''
  }

  const time = SfTime(props.preparation.time).format(dateFormat)
  return `${I18n.t('reconciliation_preparation.prepared_by')}<br />${
    props.preparation.name
  } (${time})`
})

const reviewIconComponent = computed(() =>
  getReviewCounterIcon(
    props.hasUpToDateReviewByUser,
    props.reviewsToShow.length
  )
)

const reviewTextClass = computed(() => {
  if (props.hasUpToDateReviewByUser) {
    return 'tw-text-sf-success'
  }
  return 'tw-text-sf-neutral-darker-1'
})

function markAsReviewed() {
  emit('mark-as-reviewed')
}

function unmark() {
  emit('unmark')
}
</script>

<style>
.workflow-review-status-banner .sf-banner-icon {
  display: none;
}

.workflow-review-status-banner .sf-banner-text,
.workflow-review-status-banner .sf-banner-description {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}
</style>
